import { useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { isConnectionUsable } from '@/pages/triggers/connection-state';
import { Trigger, TriggerConnection } from '@/services/triggers/types';
import { AddTriggerModal } from './add-trigger-modal';
import { TriggerRow } from './trigger-row';

const STATUS_LABELS: Record<string, string> = {
  expired: 'Authorization expired',
  degraded: 'Connection problem',
  paused: 'Paused',
};

interface ConnectionCardProps {
  connection: TriggerConnection;
  triggers: Trigger[];
  orgId?: string;
  isMine: boolean;
  // Owned by the page, not per card: two hook instances would each hold their
  // own isConnecting, so this card would offer "Finish authorization" while the
  // page's consent poll was still running and a second consent could start.
  onResumeConsent: (connectionId: string) => void;
  isConnecting: boolean;
  onRequestDisconnect: (connection: TriggerConnection) => void;
  onRequestDeleteTrigger: (trigger: Trigger) => void;
}

export const ConnectionCard = ({
  connection,
  triggers,
  orgId,
  isMine,
  onResumeConsent,
  isConnecting,
  onRequestDisconnect,
  onRequestDeleteTrigger,
}: ConnectionCardProps) => {
  const [isAddOpen, setIsAddOpen] = useState(false);

  const awaitingConsent = connection.status === 'pending_auth';
  // Anything expired/degraded/paused cannot reach Drive, so it must not read as
  // healthy — and watching a new folder on it would only queue failures.
  const isUsable = isConnectionUsable(connection);
  const connectedBy = connection.config?.connected_by_email;

  const handleResumeConsent = () => onResumeConsent(connection.id);

  return (
    <section className='space-y-3 rounded-lg border p-4'>
      <div className='flex items-start justify-between gap-4'>
        <div className='min-w-0'>
          <div className='flex items-center gap-2'>
            <span className='font-medium'>Google Drive</span>
            {awaitingConsent && (
              <Badge variant='outline' className='gap-1'>
                <AlertCircle className='size-3' />
                Awaiting authorization
              </Badge>
            )}
            {isUsable && (
              <Badge variant='secondary' className='gap-1'>
                <CheckCircle2 className='size-3' />
                Connected
              </Badge>
            )}
            {!awaitingConsent && !isUsable && (
              <Badge variant='destructive' className='gap-1'>
                <AlertCircle className='size-3' />
                {STATUS_LABELS[connection.status] ?? connection.status}
              </Badge>
            )}
            {isMine && <Badge variant='outline'>You</Badge>}
          </div>
          <p className='text-xs text-muted-foreground'>
            {connectedBy
              ? `Google account connected by ${connectedBy}`
              : 'Connected by an unknown user'}
          </p>
          <p className='text-xs text-muted-foreground'>
            Runs started from these folders are attributed to that person.
            Shared Drives are not supported yet.
          </p>
        </div>

        <div className='flex shrink-0 gap-2'>
          {!isUsable && isMine && (
            <Button onClick={handleResumeConsent} disabled={isConnecting}>
              {isConnecting && <Loader2 className='mr-2 size-4 animate-spin' />}
              {awaitingConsent ? 'Finish authorization' : 'Reconnect'}
            </Button>
          )}
          {isUsable && (
            <Button variant='outline' onClick={() => setIsAddOpen(true)}>
              <Plus className='mr-2 size-4' />
              Watch a folder
            </Button>
          )}
          <Button
            variant='outline'
            onClick={() => onRequestDisconnect(connection)}
          >
            Disconnect
          </Button>
        </div>
      </div>

      {isUsable && (
        <div className='space-y-2'>
          {triggers.length === 0 ? (
            <p className='rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground'>
              No folders watched on this account yet.
            </p>
          ) : (
            triggers.map((trigger) => (
              <TriggerRow
                key={trigger.id}
                trigger={trigger}
                orgId={orgId}
                onRequestDelete={onRequestDeleteTrigger}
              />
            ))
          )}
        </div>
      )}

      <AddTriggerModal
        open={isAddOpen}
        onOpenChange={setIsAddOpen}
        orgId={orgId}
        connection={connection}
      />
    </section>
  );
};
